import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Container, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { t } from 'i18next';

import { trackClickRegenerate } from '../utils/analytics/analytics';
import { trackSentryError } from '../utils/analytics/sentry';
import {
  handleApiError as handleApiErrorUtil,
  regenerateItemStructured,
} from '../utils/api';
import { generateImage, deleteImage } from '../utils/api_image';
import { colors } from '../utils/colors';
import { DEFAULT_GENERATION_COST } from '../utils/const';
import { formatCustomDistanceToNow } from '../utils/dateUtils';
import { useFirebaseLoginState } from '../utils/hooks';
import { RootState } from '../utils/store';
import { LoadingStatus, StructuredItem, SectionName, TabName } from '../utils/types';
import { GradientCircularProgress } from './minicomponents/GradientCircularProgress';
import { InfoTooltipView } from './minicomponents/InfoTooltipView';
import {
  IconError,
  IconKeyboardArrowDown,
  IconNotes,
  IconReload,
} from './minicomponents/icons';
import { ImageGallery } from './ImageGallery';
import { ButtonWithIcon, CopyButton } from './minicomponents/minicomponents';
import AlertPopup from './popups/AlertPopup';
import RegeneratePopup from './popups/RegeneratePopup';
import UpgradePopup from './popups/UpgradePopup';

interface GeneratedPostProps {
  post: StructuredItem;
  isNew?: boolean;
  sectionName?: SectionName;
  tabName?: TabName;
}

const generationCost = DEFAULT_GENERATION_COST;

export const GeneratedPost: React.FC<GeneratedPostProps> = ({
  post,
  isNew = false,
  sectionName = SectionName.SMM,
  tabName,
}) => {
  const { userId } = useFirebaseLoginState();
  const state = useSelector((state: RootState) => state.appState);
  const creditsLeft = state?.subcription?.credits || 0;
  const selectedCampaignId = useSelector(
    (state: RootState) => state.appState.selectedCampaign
  )?.id;

  const [activeVersionIndex, setActiveVersionIndex] = useState(0);
  const sortedVersions = useMemo(
    () =>
      post.versions
        ? [...post.versions].sort(
            (a, b) => (b.created_at?.getTime() ?? 0) - (a.created_at?.getTime() ?? 0)
          )
        : [],
    [post.versions]
  );
  const activeVersion = sortedVersions[activeVersionIndex];
  const totalVersions = sortedVersions.length;
  const hasMultipleVersions = totalVersions > 1;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [regeneratePopupOpen, setRegeneratePopupOpen] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [upgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const isPostLoading = post.status === LoadingStatus.LOADING;
  const isPostFailed = post.status === LoadingStatus.FAILED;

  const handleRegenerateButtonClick = () => {
    // Track regenerate click analytics
    if (sectionName && tabName) {
      const itemName = post.type;
      trackClickRegenerate(sectionName, tabName, itemName, totalVersions);
    }

    // Failed items get free regeneration and bypass popup, similar to normal items
    if (isPostFailed) {
      // Regenerate immediately with the last prompt (or empty string if none)
      const lastPrompt = activeVersion?.prompt || '';
      handleRegenerateSubmit(lastPrompt);
      return;
    }

    // For successful items, check credits and show popup
    if (creditsLeft < generationCost) {
      setUpgradePopupOpen(true);
      return;
    }

    setRegeneratePopupOpen(true);
  };

  const handleError = useCallback((error: unknown) => {
    trackSentryError('Error regenerating item', { error });
    handleApiErrorUtil({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: setRegenerateError,
      onSetErrorAlertOpen: (isOpen) => {
        if (!isOpen) setRegenerateError(null);
      },
    });
  }, []);

  const handleRegenerateSubmit = async (prompt: string) => {
    setRegeneratePopupOpen(false); // Close popup immediately
    if (!userId || !selectedCampaignId || !post.id || !post.structured_group_id) {
      console.error('Missing required IDs for regeneration', {
        userId,
        selectedCampaignId,
        post,
      });
      return;
    }

    setIsRegenerating(true);
    setRegenerateError(null);

    try {
      // The API call is fire-and-forget. The Firestore listener will update the UI.
      await regenerateItemStructured(
        userId,
        selectedCampaignId,
        post.structured_group_id,
        post.id,
        state.session,
        prompt,
        state.creativity
      );
    } catch (error) {
      setIsRegenerating(false);
      handleError(error);
    }
  };

  const handleGenerateImageClick = async (retry: boolean = false) => {
    if (!userId || !post.campaign_id || !post.structured_group_id || !post.id) {
      console.error('Missing required IDs for image generation', {
        userId,
        campaign_id: post.campaign_id,
        structured_group_id: post.structured_group_id,
        item_id: post.id,
      });
      setImageError('Missing required data for image generation');
      return;
    }

    const imageIdea = content?.image_idea;
    if (!imageIdea) {
      console.error('Missing image_idea for image generation', { post });
      setImageError('No image idea found for this post');
      return;
    }

    try {
      setImageError(null);

      await generateImage(
        userId,
        post.campaign_id,
        post.structured_group_id,
        post.id,
        imageIdea,
        state.session,
        retry
      );

      // Success handled by Firestore listener
    } catch (error: any) {
      console.error('Image generation failed:', error);
      setImageError(error.message || 'Failed to generate image');
      trackSentryError('IMAGE_GENERATION_ERROR', {
        campaign_id: post.campaign_id,
        item_id: post.id,
        error: error.message || 'Unknown error',
      });
    }
  };

  const handleRetryImage = async (_imageId: string) => {
    // Retry image generation - reuse the same logic as generate with retry flag
    await handleGenerateImageClick(true);
  };

  const handleDeleteImage = async (imageId: string) => {
    if (!userId || !post.campaign_id || !post.structured_group_id || !post.id) {
      console.error('Missing required IDs for image deletion', {
        userId,
        campaign_id: post.campaign_id,
        structured_group_id: post.structured_group_id,
        item_id: post.id,
      });
      setImageError('Missing required data for image deletion');
      return;
    }

    try {
      await deleteImage(
        userId,
        post.campaign_id,
        post.structured_group_id,
        post.id,
        imageId
      );

      // Success handled by Firestore listener
    } catch (error: any) {
      console.error('Image deletion failed:', error);
      setImageError(error.message || 'Failed to delete image');
      trackSentryError('IMAGE_DELETION_ERROR', {
        campaign_id: post.campaign_id,
        item_id: post.id,
        image_id: imageId,
        error: error.message || 'Unknown error',
      });
    }
  };

  // Clear regeneration loading state when post status changes
  useEffect(() => {
    if (isRegenerating && !isPostLoading) {
      setIsRegenerating(false);
    }
  }, [isRegenerating, isPostLoading]);

  const handleVersionMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleVersionMenuClose = () => setAnchorEl(null);
  const handleVersionSelect = (index: number) => {
    setActiveVersionIndex(index);
    handleVersionMenuClose();
  };
  const handleOlderVersion = () => {
    if (activeVersionIndex < totalVersions - 1)
      setActiveVersionIndex(activeVersionIndex + 1);
  };
  const handleNewerVersion = () => {
    if (activeVersionIndex > 0) setActiveVersionIndex(activeVersionIndex - 1);
  };
  const handleNotes = () => {};

  const content = activeVersion?.structured_content;

  if (!content) {
    return <Typography>{t('app.errors.failedToGenerate')}</Typography>;
  }

  const fullTextToCopy = `${content.headline}\n\n${content.body}`;

  return (
    <Container
      sx={{
        padding: '16px 40px 24px',
        background: isPostFailed ? 'rgba(229, 57, 53, 0.1)' : '#F8F8FA',
        borderRadius: '6px',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="end"
        sx={{ height: '40px' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '90%', color: 'text.secondary' }}>
            {t('app.structuredItems.types.' + post.type)}
          </Typography>
          {isNew && !isPostLoading && (
            <Typography
              variant="h6"
              sx={{
                background: colors.success,
                color: 'white',
                px: 1,
                borderRadius: '2px',
                fontWeight: 'bold',
                fontSize: '80%',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                // textShadow: '0 1px 1px rgba(0,0,0,0.2)',
              }}
            >
              {t('app.structuredItems.newItemLabel')}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          sx={{ visibility: isPostLoading ? 'hidden' : 'visible' }}
        >
          {!isPostFailed && hasMultipleVersions && (
            <>
              <InfoTooltipView
                text={t('app.labels.previousVersion')}
                icon={
                  <ButtonWithIcon
                    icon={<IconKeyboardArrowDown sx={{ rotate: '90deg' }} />}
                    onClick={handleOlderVersion}
                    isDisabled={activeVersionIndex === totalVersions - 1}
                  />
                }
              />
              <InfoTooltipView
                text={t('app.labels.selectVersion')}
                icon={
                  <ButtonWithIcon
                    onClick={handleVersionMenuOpen}
                    icon={
                      <Typography
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        {t('app.campaign.version', {
                          current: totalVersions - activeVersionIndex,
                          total: totalVersions,
                        })}
                      </Typography>
                    }
                    sx={{ minWidth: 90 }}
                    isAccentOnHover={true}
                  />
                }
              />
              <Menu
                id="version-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleVersionMenuClose}
              >
                {sortedVersions.map((version, index) => (
                  <MenuItem
                    key={version.version}
                    onClick={() => handleVersionSelect(index)}
                  >
                    <Typography
                      fontWeight={index === activeVersionIndex ? 'bold' : 'normal'}
                    >{`${
                      totalVersions - index
                    }) ${formatCustomDistanceToNow(version.created_at!)}`}</Typography>
                  </MenuItem>
                ))}
              </Menu>
              <InfoTooltipView
                text={
                  activeVersion.prompt
                    ? t('app.labels.remarkLabel') + ' ' + activeVersion.prompt
                    : t('app.labels.noRemarkProvided')
                }
                icon={
                  <ButtonWithIcon
                    icon={<IconNotes />}
                    onClick={handleNotes}
                    isAccentOnHover={false}
                    isDisabled={!activeVersion?.prompt}
                  />
                }
              />
              <InfoTooltipView
                text={t('app.labels.nextVersion')}
                icon={
                  <ButtonWithIcon
                    icon={<IconKeyboardArrowDown sx={{ rotate: '270deg' }} />}
                    onClick={handleNewerVersion}
                    isDisabled={activeVersionIndex === 0}
                  />
                }
              />
            </>
          )}
          {!isPostFailed && (
            <InfoTooltipView
              icon={
                <CopyButton
                  textToCopy={fullTextToCopy}
                  sectionName={sectionName}
                  tabName={tabName}
                  itemName={post.type}
                />
              }
              text={t('app.popups.copy.tooltipGeneric')}
            />
          )}
          <InfoTooltipView
            icon={
              <ButtonWithIcon
                icon={
                  isRegenerating ? (
                    <GradientCircularProgress size={16} />
                  ) : (
                    <IconReload />
                  )
                }
                onClick={handleRegenerateButtonClick}
                isDisabled={isRegenerating}
              />
            }
            text={
              isPostFailed
                ? t('app.popups.regenerate.recoverText')
                : t('app.popups.regenerate.customizeText', { title: content.headline })
            }
          />
        </Stack>
      </Stack>

      {isPostFailed && (
        <Box
          sx={{
            my: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconError sx={{ color: 'error.main', mr: 1 }} />
          <Typography variant="body2">
            <span style={{ color: colors.text.error }}>Failed to generate</span>{' '}
            <span style={{ color: colors.text.hint }}>
              (we do not charge credits for failed generations)
            </span>
          </Typography>
        </Box>
      )}

      {isPostLoading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 1 }}>
          <GradientCircularProgress size={16} />
          <Typography sx={{ color: colors.text.hint, fontWeight: 'medium' }}>
            {t('app.structuredItems.actions.regeneratePost.progressMessage')}
          </Typography>
        </Stack>
      ) : (
        !isPostFailed && (
          <Box>
            <Typography variant="h2" gutterBottom>
              {content.headline}
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {content.body}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <ImageGallery
                images={content.images || []}
                onGenerate={handleGenerateImageClick}
                onDelete={handleDeleteImage}
                onRetry={handleRetryImage}
                disabled={post.status === LoadingStatus.LOADING}
                creditsLeft={creditsLeft}
              />
              {imageError && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'error.main',
                    mt: 1,
                  }}
                >
                  {imageError}
                </Typography>
              )}
            </Box>
          </Box>
        )
      )}
      <RegeneratePopup
        open={regeneratePopupOpen}
        onClose={() => setRegeneratePopupOpen(false)}
        onSubmit={handleRegenerateSubmit}
        title={t('app.popups.regenerate.title', { title: content.headline })}
        buttonText={
          isPostFailed
            ? t('app.buttons.retry')
            : t('app.popups.regenerate.buttonText', { count: 1 })
        }
        generationCost={isPostFailed ? 0 : generationCost}
        creditsLeft={creditsLeft}
        isRemarksMandatory={false}
        sectionName={sectionName}
        tabName={tabName}
        itemName={post.type}
        creativityLevel={state.creativity}
        regenerationsCount={totalVersions}
      />
      <AlertPopup
        open={!!regenerateError}
        onClose={() => setRegenerateError(null)}
        onSubmit={() => setRegenerateError(null)}
        buttonText={t('app.buttons.ok')}
        alertText={regenerateError || ''}
        title={t('app.popups.error.title')}
      />
      <UpgradePopup
        open={upgradePopupOpen}
        onClose={() => setUpgradePopupOpen(false)}
        onError={handleError}
      />
    </Container>
  );
};
